/**
 * Opening-hours evaluation: is a place open at a given moment, given Google-style
 * weekly `periods`?
 *
 * Shared (not re-derived) so web and native compute "Open now" identically, including the
 * fiddly overnight / week-rollover / 24-7 edges. No React / Firebase imports (React-Native /
 * Metro-bundlable per the `@elara/domain` portability guard).
 */

import type { PlaceOpeningPeriod } from './models/schemas/place-schemas.js';

const MINUTES_PER_DAY = 24 * 60;
const WEEK_MINUTES = 7 * MINUTES_PER_DAY;

/** Absolute minute-of-week (0 = Sunday 00:00) for a day/hour/minute triple. */
function minuteOfWeek(day: number, hour: number, minute: number): number {
  return day * MINUTES_PER_DAY + hour * 60 + minute;
}

/**
 * Returns true when at least one opening `period` contains the instant `now`.
 *
 * `now` is interpreted by its **local** weekday/hour/minute (`getDay`/`getHours`/`getMinutes`),
 * so the caller is responsible for passing a `Date` already shifted into the place's own time
 * zone — this function does no time-zone math itself. Days follow Google's convention
 * (0 = Sunday … 6 = Saturday).
 *
 * Edges handled:
 * - **24/7**: a single period that opens Sunday 00:00 with no `close` is always open.
 * - **Overnight / week rollover**: a period whose close is at or before its open (e.g. opens
 *   Fri 20:00, closes Sat 02:00, or a span wrapping Sat→Sun) is treated as crossing the week
 *   boundary, so `now` matches if it is after the open OR before the wrapped close.
 * - Periods with any missing open/close field are skipped (can't be evaluated safely).
 *
 * Returns false when there are no periods.
 */
export function isOpenAt(periods: PlaceOpeningPeriod[] | null | undefined, now: Date): boolean {
  if (!periods?.length) return false;

  const nowMinutes = minuteOfWeek(now.getDay(), now.getHours(), now.getMinutes());

  for (const period of periods) {
    const open = period.open;
    const close = period.close;

    // 24/7: single period opening Sunday 00:00 with no close.
    if (open?.day === 0 && open.hour === 0 && open.minute === 0 && !close) {
      return true;
    }

    if (
      open?.day == null ||
      open.hour == null ||
      open.minute == null ||
      close?.day == null ||
      close.hour == null ||
      close.minute == null
    ) {
      continue;
    }

    const openMinutes = minuteOfWeek(open.day, open.hour, open.minute);
    let closeMinutes = minuteOfWeek(close.day, close.hour, close.minute);

    // A close at or before the open means the period wraps past the week boundary.
    const crossesWeekBoundary = closeMinutes <= openMinutes;
    if (crossesWeekBoundary) {
      closeMinutes += WEEK_MINUTES;
    }

    if (crossesWeekBoundary) {
      const rolloverClose = closeMinutes - WEEK_MINUTES;
      if (nowMinutes >= openMinutes || nowMinutes < rolloverClose) {
        return true;
      }
    } else if (nowMinutes >= openMinutes && nowMinutes < closeMinutes) {
      return true;
    }
  }

  return false;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Formats a 24h time as a compact 12h string ("9 AM", "8:30 PM", "Midnight", "Midday"). Pure
 * arithmetic — no `Date`/`Intl` involved — so it can't be skewed by a JS engine's historical
 * timezone data for an arbitrary anchor date (`OpeningHoursTable` hit exactly that on RN/Hermes:
 * `new Date(1970, 0, 1, hour, minute)` came out an hour off from the raw `hour`/`minute` on some
 * devices, since 1970's UTC offset for the device's zone isn't guaranteed to match today's).
 */
export function formatTimeOfDay(hour: number, minute: number): string {
  if (hour === 0 && minute === 0) return 'Midnight';
  if (hour === 12 && minute === 0) return 'Midday';
  const meridiem = hour < 12 ? 'AM' : 'PM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return minute === 0 ? `${hour12} ${meridiem}` : `${hour12}:${minute.toString().padStart(2, '0')} ${meridiem}`;
}

interface OpeningChange {
  type: 'opens' | 'closes';
  day: number; // 0 = Sunday … 6 = Saturday
  hour: number;
  minute: number;
}

/**
 * The next open→closed or closed→open transition after `now`, or null when there are no usable
 * periods or the place never changes state (e.g. 24/7). `now` is read by its local weekday/time,
 * matching `isOpenAt` — the caller shifts it into the place's time zone.
 */
function nextOpeningChange(periods: PlaceOpeningPeriod[] | null | undefined, now: Date): OpeningChange | null {
  if (!periods?.length) return null;

  const nowMinutes = minuteOfWeek(now.getDay(), now.getHours(), now.getMinutes());
  const events: Array<{ at: number; type: 'opens' | 'closes' }> = [];

  for (const period of periods) {
    const open = period.open;
    const close = period.close;

    // 24/7 (opens Sunday 00:00 with no close) never transitions.
    if (open?.day === 0 && open.hour === 0 && open.minute === 0 && !close) continue;

    if (open?.day != null && open.hour != null && open.minute != null) {
      events.push({ at: minuteOfWeek(open.day, open.hour, open.minute), type: 'opens' });
    }
    if (close?.day != null && close.hour != null && close.minute != null) {
      events.push({ at: minuteOfWeek(close.day, close.hour, close.minute), type: 'closes' });
    }
  }

  if (events.length === 0) return null;

  events.sort((a, b) => a.at - b.at);
  // The first event strictly after now, wrapping to next week's earliest if none remain this week.
  const next = events.find((event) => event.at > nowMinutes) ?? events[0]!;
  const at = ((next.at % WEEK_MINUTES) + WEEK_MINUTES) % WEEK_MINUTES;

  return {
    type: next.type,
    day: Math.floor(at / MINUTES_PER_DAY),
    hour: Math.floor((at % MINUTES_PER_DAY) / 60),
    minute: at % 60,
  };
}

export interface OpeningStatus {
  openNow: boolean;
  /**
   * A human description of the next transition, Google-Maps style: "Closes 5 PM" (same day) or
   * "Opens 8:30 AM Thu" (a later day). Undefined when there's no usable schedule or the place is 24/7.
   */
  detail?: string;
}

/**
 * Summarises a place's opening hours relative to `now`: whether it's open, plus a short description
 * of the next open/close transition. `now` must already be shifted into the place's time zone.
 */
export function describeOpeningStatus(periods: PlaceOpeningPeriod[] | null | undefined, now: Date): OpeningStatus {
  const openNow = isOpenAt(periods, now);
  const change = nextOpeningChange(periods, now);
  if (!change) return { openNow };

  const verb = change.type === 'opens' ? 'Opens' : 'Closes';
  const time = formatTimeOfDay(change.hour, change.minute);
  const detail = change.day === now.getDay() ? `${verb} ${time}` : `${verb} ${time} ${DAY_NAMES[change.day]}`;

  return { openNow, detail };
}
